/**
 * Auto-generate model configurations from the complete model list
 */

import { ALL_176_CF_MODELS } from './all-176-models.js';

// Model metadata by pattern
const MODEL_METADATA = {
  // Context windows
  'llama-3.3': { contextWindow: 128000 },
  'llama-3.2': { contextWindow: 128000 },
  'llama-3.1': { contextWindow: 128000 },
  'llama-3': { contextWindow: 8192 },
  'llama-2': { contextWindow: 4096 },
  'mistral': { contextWindow: 8192 },
  'mixtral': { contextWindow: 32768 },
  'qwen': { contextWindow: 32768 },
  'gemma': { contextWindow: 8192 },
  'phi': { contextWindow: 4096 },
  'deepseek': { contextWindow: 16384 },
  'codellama': { contextWindow: 16384 },
  'starcoder': { contextWindow: 16384 },
  'whisper': { type: 'speech-to-text' },
  'stable-diffusion': { type: 'image-generation' },
  'bge': { type: 'embeddings', dimensions: 768 },
  'sentence-transformers': { type: 'embeddings', dimensions: 384 },
  'detr': { type: 'object-detection' },
  'resnet': { type: 'image-classification' },
  'vit': { type: 'image-classification' },
  'distilbert': { type: 'text-classification' },
  'm2m100': { type: 'translation' },
  'llava': { type: 'vision', contextWindow: 4096 }
};

// Extract model type from ID
function getModelType(modelId) {
  const lower = modelId.toLowerCase();

  // Vision models
  if (lower.includes('vision') || lower.includes('llava') || lower.includes('uform')) {
    return 'vision';
  }

  // Embeddings
  if (lower.includes('bge') || lower.includes('sentence-transformers') || lower.includes('embedding')) {
    return 'embeddings';
  }

  // Speech
  if (lower.includes('whisper')) {
    return 'speech-to-text';
  }

  // Translation
  if (lower.includes('m2m100') || lower.includes('mbart') || lower.includes('nllb')) {
    return 'translation';
  }

  // Image generation
  if (lower.includes('stable-diffusion') || lower.includes('dream')) {
    return 'image-generation';
  }

  // Classification
  if (lower.includes('distilbert') || lower.includes('resnet') || lower.includes('vit') ||
      lower.includes('efficientnet') || lower.includes('mobilenet') || lower.includes('detr')) {
    return 'classification';
  }

  // Default: text generation
  return 'text-generation';
}

// Extract context window
function getContextWindow(modelId) {
  const lower = modelId.toLowerCase();

  for (const [pattern, meta] of Object.entries(MODEL_METADATA)) {
    if (lower.includes(pattern) && meta.contextWindow) {
      return meta.contextWindow;
    }
  }

  // Defaults
  if (lower.includes('70b')) return 128000;
  if (lower.includes('7b') || lower.includes('8b')) return 8192;
  if (lower.includes('13b')) return 16384;
  if (lower.includes('34b')) return 32768;

  return 4096; // conservative default
}

// Generate nice display name
function getDisplayName(modelId) {
  // Remove namespace
  const withoutNamespace = modelId.replace('@cf/', '').replace('@hf/', '');

  // Extract parts
  const parts = withoutNamespace.split('/');
  const modelName = parts[parts.length - 1];

  // Clean up
  return modelName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/Hf$/, 'HF')
    .replace(/Awq/g, 'AWQ')
    .replace(/Int8/, 'INT8')
    .replace(/Fp16/, 'FP16')
    .replace(/Lcm/, 'LCM');
}

// Detect capabilities
function getCapabilities(modelId) {
  const lower = modelId.toLowerCase();
  const caps = [];

  // Size
  if (lower.includes('70b')) caps.push('powerful');
  if (lower.includes('34b')) caps.push('large-model');
  if (lower.includes('1b') || lower.includes('0.5b')) caps.push('tiny-model');

  // Specialization
  if (lower.includes('coder') || lower.includes('code')) caps.push('coding');
  if (lower.includes('math')) caps.push('math');
  if (lower.includes('sql')) caps.push('sql');
  if (lower.includes('vision')) caps.push('vision');
  if (lower.includes('instruct')) caps.push('instruction-following');
  if (lower.includes('chat')) caps.push('conversational');

  // Optimization
  if (lower.includes('awq')) caps.push('quantized', 'fast');
  if (lower.includes('int8')) caps.push('quantized', 'fast');
  if (lower.includes('fp16')) caps.push('half-precision');
  if (lower.includes('turbo') || lower.includes('lightning')) caps.push('ultra-fast');

  // Multimodal
  if (lower.includes('multilingual')) caps.push('multilingual');

  return caps.length > 0 ? caps : ['general'];
}

// Generate model configurations
export function generateAllModelConfigs() {
  const configs = {};

  ALL_176_CF_MODELS.forEach(modelId => {
    const type = getModelType(modelId);
    const key = modelId.replace(/[@\/\.\-]/g, '_').toUpperCase();

    const config = {
      id: modelId,
      name: getDisplayName(modelId),
      type: type
    };

    // Add type-specific fields
    if (type === 'text-generation' || type === 'vision') {
      config.contextWindow = getContextWindow(modelId);
      config.capabilities = getCapabilities(modelId);
    }

    if (type === 'embeddings') {
      config.dimensions = modelId.includes('large') ? 1024 :
                        modelId.includes('small') ? 384 : 768;
      config.maxTokens = 512;
    }

    configs[key] = config;
  });

  return configs;
}

// Export as organized categories
export function organizeModels() {
  const organized = {
    TEXT_GENERATION: {},
    VISION: {},
    EMBEDDINGS: {},
    SPEECH: {},
    TRANSLATION: {},
    IMAGE_GEN: {},
    CLASSIFICATION: {}
  };

  const allConfigs = generateAllModelConfigs();

  Object.entries(allConfigs).forEach(([key, config]) => {
    switch (config.type) {
      case 'text-generation':
        organized.TEXT_GENERATION[key] = config;
        break;
      case 'vision':
        organized.VISION[key] = config;
        break;
      case 'embeddings':
        organized.EMBEDDINGS[key] = config;
        break;
      case 'speech-to-text':
        organized.SPEECH[key] = config;
        break;
      case 'translation':
        organized.TRANSLATION[key] = config;
        break;
      case 'image-generation':
        organized.IMAGE_GEN[key] = config;
        break;
      case 'classification':
      case 'object-detection':
      case 'image-classification':
        organized.CLASSIFICATION[key] = config;
        break;
    }
  });

  return organized;
}

// Get flat list for model selector
export function getAllModelsFlat() {
  const configs = generateAllModelConfigs();
  return Object.values(configs);
}
